'use client';

import dynamic from 'next/dynamic';
import type { ComponentType } from 'react';
import { normalizeLocale } from '@/i18n/config';
import translations from '@/i18n/marketing-showcase-copy.json';
import businessTranslations from '@/i18n/marketing-business-showcase-copy.json';
import { ShowcaseCarousel } from '../landing-showcase-carousel';
import { ShowcaseCopyContext } from '../collection-showcase/translation';
import { useMostlyInView } from '../collection-showcase/use-animation-clock';
import '../collection-showcase/styles.css';
import './styles.css';

type BusinessSceneProps = {
  running: boolean;
  onInteract: () => void;
  onDone: () => void;
};

const scenes = [
  {
    id: 'your-imai',
    title: 'Your IMAI',
    View: dynamic<BusinessSceneProps>(() =>
      import('./scenes/your-imai-animation').then((m) => m.YourImaiAnimation)
    ),
  },
  {
    id: 'apps',
    title: 'Apps',
    View: dynamic<BusinessSceneProps>(() =>
      import('./scenes/apps-animation').then((m) => m.AppsAnimation)
    ),
  },
  {
    id: 'schedule',
    title: 'Schedule',
    View: dynamic<BusinessSceneProps>(() =>
      import('./scenes/schedule-animation').then((m) => m.ScheduleAnimation)
    ),
  },
  {
    id: 'finance',
    title: 'Finance',
    View: dynamic<BusinessSceneProps>(() =>
      import('./scenes/finance-animation').then((m) => m.FinanceAnimation)
    ),
  },
  {
    id: 'approvals',
    title: 'Approvals',
    View: dynamic<BusinessSceneProps>(() =>
      import('./scenes/approvals-animation').then((m) => m.ApprovalsAnimation)
    ),
  },
] as const;

const keepCurrentSlide = () => {};

function BusinessWindow({ View }: { View: ComponentType<BusinessSceneProps> }) {
  const [attach, visible] = useMostlyInView(0.45);
  return (
    <div ref={attach} className="collection-showcase business-showcase">
      <div className="sf rounded-[20px] p-2">
        <div className="business-window relative min-w-0 rounded-[12px] bg-[var(--canvas)] shadow-[inset_0_0_0_1px_var(--line)]">
          <View
            running={visible === true}
            onInteract={keepCurrentSlide}
            onDone={keepCurrentSlide}
          />
        </div>
      </div>
    </div>
  );
}

export function BusinessShowcase({
  locale,
  label,
  controls,
}: {
  locale: string;
  label: string;
  controls: { previous: string; next: string };
}) {
  const language = normalizeLocale(locale);
  const copy = { ...translations[language], ...businessTranslations[language] };
  return (
    <ShowcaseCopyContext value={copy}>
      <ShowcaseCarousel
        label={label}
        roleDescription={copy.carousel}
        previousLabel={controls.previous}
        nextLabel={controls.next}
        slides={scenes.map(({ id, title, View }) => ({
          id,
          label: copy[title],
          content: <BusinessWindow View={View} />,
        }))}
      />
    </ShowcaseCopyContext>
  );
}
